/**
 * @module ui/MicroVictory
 * @description Standardized "Micro-Victory" visual language used across YUMIX.
 *
 * Single source of truth for the orange-glow / dashed-target / streak-flame
 * pattern Yuri validated on the Walking page (2026-05-05). Plug this into any
 * progress metric (protéines, pas, hydratation, sommeil...) so every win the
 * user sees feels like the same dopaminergic signal.
 *
 * Visual contract:
 *   • HIT  → orange fluo gradient + ambient glow + amber tick label
 *   • MISS → flat dark zinc, no glow, no warmth
 *   • Goal → dashed amber line + "CIBLE : N" badge
 *   • Streak → animated 🔥 in top-left when 3+ trailing hits
 *
 * Components exported:
 *   - <MicroVictoryBar />   single horizontal/vertical bar (KPI tile)
 *   - <MicroVictoryHisto /> multi-bar histogram (week / month / year)
 *   - <TripleVictoryWidget /> three-stat dashboard widget
 *   - cn helper / palette tokens for consistent styling
 */
import React, { useEffect, useRef } from 'react';
import { Footprints, Zap, Dumbbell, Flame, Trophy } from 'lucide-react';

/**
 * Play a short "racing vroom" chirp using the Web Audio API.
 * No audio file required — uses two oscillators (sawtooth + sine) with a
 * descending pitch sweep + quick decay (~280ms). Respects
 * localStorage `yumix_sfx_muted` so the user can silence it globally.
 */
function playVictoryVroom() {
  try {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem('yumix_sfx_muted') === '1') return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const t0 = ctx.currentTime;

    // Main racing oscillator (sawtooth, descending sweep 520Hz → 180Hz)
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(520, t0);
    osc.frequency.exponentialRampToValueAtTime(180, t0 + 0.28);

    // High sparkle layer (sine, short blip)
    const sparkle = ctx.createOscillator();
    sparkle.type = 'sine';
    sparkle.frequency.setValueAtTime(880, t0);
    sparkle.frequency.exponentialRampToValueAtTime(440, t0 + 0.18);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.30);

    const sparkleGain = ctx.createGain();
    sparkleGain.gain.setValueAtTime(0.0001, t0);
    sparkleGain.gain.exponentialRampToValueAtTime(0.10, t0 + 0.02);
    sparkleGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.20);

    osc.connect(gain).connect(ctx.destination);
    sparkle.connect(sparkleGain).connect(ctx.destination);

    osc.start(t0);
    sparkle.start(t0);
    osc.stop(t0 + 0.32);
    sparkle.stop(t0 + 0.22);

    // Auto-close context to free resources
    setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, 500);
  } catch (_) {
    /* best-effort — never crash UI on audio failure */
  }
}

/**
 * Haptic tap on supported mobile devices (Android Chrome, etc).
 * Pattern = quick double-tap → feels like a "racing flag" kick.
 */
function triggerVictoryHaptic() {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([35, 40, 90]);
    }
  } catch (_) { /* noop */ }
}

/**
 * Shared hook — fires vroom + haptic exactly on the transition from
 * "not hit" → "hit". Skips the first render (to avoid a ghost trigger
 * when the user lands on the page already validated).
 */
function useVictoryFx(hit) {
  const prev = useRef(null); // null = first render, not an actual transition
  useEffect(() => {
    if (prev.current === null) {
      prev.current = hit;
      return;
    }
    if (!prev.current && hit) {
      playVictoryVroom();
      triggerVictoryHaptic();
    }
    prev.current = hit;
  }, [hit]);
}

const FLAME_KEYFRAMES = `
  @keyframes mv-flame-pulse {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 4px rgba(251,146,60,0.6)); }
    50% { transform: scale(1.08); filter: drop-shadow(0 0 10px rgba(251,146,60,0.95)); }
  }
  @keyframes mv-glow-pulse {
    0%, 100% { box-shadow: 0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45); }
    50%      { box-shadow: 0 0 22px rgba(251,146,60,0.75), 0 0 8px rgba(245,158,11,0.65); }
  }
  @keyframes mv-racing-pulse {
    0%, 100% { box-shadow: 0 0 16px rgba(239,68,68,0.55), 0 0 5px rgba(220,38,38,0.45), inset 0 0 0 1px rgba(239,68,68,0.18); }
    50%      { box-shadow: 0 0 26px rgba(239,68,68,0.78), 0 0 10px rgba(220,38,38,0.65), inset 0 0 0 1px rgba(239,68,68,0.30); }
  }
  @keyframes mv-track-pulse {
    0%, 100% { box-shadow: 0 0 10px rgba(239,68,68,0.45), 0 0 3px rgba(248,113,113,0.35), inset 0 0 6px rgba(0,0,0,0.65); }
    50%      { box-shadow: 0 0 16px rgba(239,68,68,0.7),  0 0 5px rgba(248,113,113,0.55), inset 0 0 6px rgba(0,0,0,0.65); }
  }
`;

let _injected = false;
function injectKeyframes() {
  if (_injected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-mv-keyframes', '1');
  style.textContent = FLAME_KEYFRAMES;
  document.head.appendChild(style);
  _injected = true;
}

/**
 * <MicroVictoryBar /> — single horizontal progress bar for a daily KPI tile.
 *
 * Props:
 *   value, target  – numbers
 *   label          – top label ("Protéines", "Pas", ...)
 *   unit           – appended to the value ("g", "min", "")
 *   icon           – optional lucide icon component
 *   pulseWhenHit   – glow pulses (used in TripleVictory bonus)
 *   testId
 */
export function MicroVictoryBar({
  value = 0,
  target = 1,
  label,
  unit = '',
  icon: Icon,
  pulseWhenHit = false,
  testId,
  language = 'fr',
}) {
  injectKeyframes();
  const fr = language === 'fr';
  const safeTarget = target > 0 ? target : 1;
  const pct = Math.min(100, (value / safeTarget) * 100);
  const hit = value >= target;
  useVictoryFx(hit);

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
          {Icon && <Icon className={`w-3 h-3 ${hit ? 'text-amber-400' : 'text-muted-foreground'}`} />}
          {label}
        </span>
        <span className={`text-xs font-black tabular-nums ${hit ? 'text-amber-300' : 'text-foreground'}`}>
          {Math.round(value).toLocaleString()}{unit}
          <span className="text-muted-foreground/70 font-normal text-[10px]"> / {Math.round(target).toLocaleString()}{unit}</span>
        </span>
      </div>
      <div
        className="relative h-2.5 w-full rounded-full bg-black/90 overflow-hidden ring-1 ring-red-500/55"
        style={{
          boxShadow: hit
            ? 'inset 0 0 6px rgba(0,0,0,0.6)'
            : '0 0 12px rgba(239,68,68,0.55), 0 0 4px rgba(248,113,113,0.45), inset 0 0 6px rgba(0,0,0,0.65)',
          animation: hit ? 'none' : 'mv-track-pulse 2.6s ease-in-out infinite',
        }}
      >
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            hit
              ? 'bg-gradient-to-r from-orange-600 via-orange-500 to-amber-300'
              : 'bg-gradient-to-r from-rose-400 via-red-500 to-red-400'
          }`}
          style={{
            width: `${Math.max(pct, 6)}%`,
            boxShadow: hit
              ? '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)'
              : '0 0 18px rgba(248,113,113,1), 0 0 8px rgba(239,68,68,0.9)',
            animation: hit && pulseWhenHit ? 'mv-glow-pulse 2.4s ease-in-out infinite' : 'none',
          }}
        />
      </div>
      {hit && (
        <p className="text-[9px] uppercase tracking-widest text-amber-400 font-bold">
          {fr ? '✓ Cible atteinte' : '✓ Goal hit'}
        </p>
      )}
    </div>
  );
}

/**
 * <TripleVictoryWidget /> — Pas + Protéines + Entraînement, side-by-side.
 *
 * "Triple Victoire" only fires when ALL THREE goals are met that day.
 * In that case the entire card pulses with a subtle global glow + crown badge.
 */
export function TripleVictoryWidget({ stats, language = 'fr', onNavigate }) {
  injectKeyframes();
  const fr = language === 'fr';

  const steps = stats?.today_steps || 0;
  const stepGoal = stats?.step_goal || 10000;
  const protein = stats?.today_protein_g || 0;
  const proteinTarget = stats?.protein_target_min || 100;
  const hasWorkout = !!stats?.has_workout_today;

  const stepsHit = steps >= stepGoal;
  const proteinHit = protein >= proteinTarget;
  const workoutHit = hasWorkout;
  const tripleHit = stepsHit && proteinHit && workoutHit;
  const hitCount = [stepsHit, proteinHit, workoutHit].filter(Boolean).length;

  return (
    <div
      className={`relative rounded-2xl p-4 transition-all duration-500 ${
        tripleHit
          ? 'bg-gradient-to-br from-amber-500/15 via-orange-500/12 to-red-500/12 ring-2 ring-amber-400/60'
          : 'bg-gradient-to-br from-red-950/55 via-zinc-950/80 to-red-950/40 ring-2 ring-red-500/60'
      }`}
      data-testid="triple-victory-widget"
      style={{
        boxShadow: tripleHit
          ? '0 0 22px rgba(251,146,60,0.6), 0 0 8px rgba(245,158,11,0.5)'
          : '0 0 20px rgba(239,68,68,0.65), 0 0 7px rgba(220,38,38,0.55), inset 0 0 0 1px rgba(239,68,68,0.20)',
        animation: tripleHit
          ? 'mv-glow-pulse 2.4s ease-in-out infinite'
          : 'mv-racing-pulse 3.2s ease-in-out infinite',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className={`w-4 h-4 ${tripleHit ? 'text-amber-400' : 'text-red-400'}`} style={{ filter: tripleHit ? 'drop-shadow(0 0 6px rgba(251,146,60,0.7))' : 'drop-shadow(0 0 5px rgba(239,68,68,0.7))' }} />
          <h3 className="font-heading text-sm uppercase tracking-wider text-foreground">
            {fr ? 'Triple Victoire' : 'Triple Win'}
          </h3>
        </div>
        <span className={`text-[10px] font-black tracking-widest ${tripleHit ? 'text-amber-400' : 'text-red-400'}`}>
          {hitCount}/3
        </span>
      </div>

      {/* Three KPI bars */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => onNavigate?.('/walking')}
          className="w-full text-left hover:opacity-90 transition-opacity"
          data-testid="triple-victory-steps"
        >
          <MicroVictoryBar
            value={steps}
            target={stepGoal}
            label={fr ? 'Pas' : 'Steps'}
            icon={Footprints}
            pulseWhenHit={tripleHit}
            language={language}
          />
        </button>
        <MicroVictoryBar
          value={protein}
          target={proteinTarget}
          label={fr ? 'Protéines' : 'Protein'}
          unit="g"
          icon={Zap}
          pulseWhenHit={tripleHit}
          language={language}
          testId="triple-victory-protein"
        />
        <WorkoutVictoryRow done={workoutHit} pulseWhenHit={tripleHit} language={language} onClick={() => onNavigate?.('/workouts')} />
      </div>

      {/* Triple-hit celebration */}
      {tripleHit && (
        <div
          className="mt-4 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-orange-600/20 to-amber-500/20 ring-1 ring-amber-400/40"
          data-testid="triple-victory-banner"
        >
          <span style={{ animation: 'mv-flame-pulse 1.4s ease-in-out infinite' }}>🔥</span>
          <span className="text-xs font-black uppercase tracking-widest text-amber-300">
            {fr ? 'Journée parfaite !' : 'Perfect day!'}
          </span>
          <span style={{ animation: 'mv-flame-pulse 1.4s ease-in-out infinite' }}>🔥</span>
        </div>
      )}
    </div>
  );
}

/**
 * Workout has no continuous progress — it's binary (done / not done) — so it
 * uses a custom row that mirrors the same visual language without a bar.
 */
function WorkoutVictoryRow({ done, pulseWhenHit, language, onClick }) {
  const fr = language === 'fr';
  useVictoryFx(done);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-2 hover:opacity-90 transition-opacity"
      data-testid="triple-victory-workout"
    >
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
        <Dumbbell className={`w-3 h-3 ${done ? 'text-amber-400' : 'text-muted-foreground'}`} />
        {fr ? 'Entraînement' : 'Workout'}
      </span>
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
          done
            ? 'bg-gradient-to-r from-orange-600 to-amber-300 text-zinc-900 ring-1 ring-amber-300/60'
            : 'bg-zinc-800/80 text-muted-foreground ring-1 ring-border/40'
        }`}
        style={{
          boxShadow: done ? '0 0 12px rgba(251,146,60,0.5)' : 'none',
          animation: done && pulseWhenHit ? 'mv-glow-pulse 2.4s ease-in-out infinite' : 'none',
        }}
      >
        {done ? (
          <>
            <Flame className="w-3 h-3" />
            {fr ? 'Fait' : 'Done'}
          </>
        ) : (
          <>
            <span className="opacity-60">{fr ? 'À faire' : 'To do'}</span>
          </>
        )}
      </span>
    </button>
  );
}

export default TripleVictoryWidget;
